import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "utils/@reduxjs/toolkit";
import { useInjectReducer } from "utils/redux-injectors";
import { createApi } from "@reduxjs/toolkit/query/react";
import { endpoints, baseQuery, formatErrors } from "utils/api/endpoints";
import {
  ActivationState,
  CheckSimAvailabilityResponse,
  ActivationCheckoutSummaryResponse,
} from "./types";

export const initialState: ActivationState = {
  loading: false,
  error: null,
};

const slice = createSlice({
  name: "activation",
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const api = createApi({
  reducerPath: "activationApi",
  baseQuery,
  tagTypes: ["Activation"],
  endpoints: (builder) => ({
    getDeviceDetails: builder.mutation<
      CheckSimAvailabilityResponse,
      { activationCode: string }
    >({
      query: (params) => ({
        ...endpoints.deviceDetails,
        params,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getActivationCheckoutSummary: builder.mutation<
      ActivationCheckoutSummaryResponse,
      {
        activationCode: string;
        planId: string;
        billing_zip: string;
        couponCode?: string;
        optForSecondMonthUpsell?: boolean;
        optForDigitalSecurityTrial?: boolean;
        digitalSecurityPlanId?: string;
        /** Only sent for no-included-plan (external) activation checkout; e.g. `"web"`. */
        source?: string;
      }
    >({
      query: (body) => ({
        // ...endpoints.fixedPhoneCheckoutExternalSummary,
        ...endpoints.fixedPhoneCheckoutExternalPrepaidSummary,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    /**
     * Fixed phone activation when the device has no plan on record (new activation; not upsell from included monthly/yearly).
     * Same request body as prepaid upsell summary; only the URL differs.
     */
    getActivationNoIncludedPlanCheckoutSummary: builder.mutation<
      ActivationCheckoutSummaryResponse,
      {
        activationCode: string;
        planId: string;
        billing_zip: string;
        couponCode?: string;
        optForSecondMonthUpsell?: boolean;
        optForDigitalSecurityTrial?: boolean;
        digitalSecurityPlanId?: string;
        source?: string;
      }
    >({
      query: (body) => ({
        ...endpoints.fixedPhoneCheckoutExternalSummary,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    createActivationSubscription: builder.mutation<any, any>({
      query: (body) => ({
        // ...endpoints.fixedPhoneCreateSubscriptionExternal,
        ...endpoints.fixedPhoneCreateSubscriptionExternalPrepaid,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    createActivationNoIncludedPlanSubscription: builder.mutation<any, any>({
      query: (body) => ({
        ...endpoints.fixedPhoneCreateSubscriptionExternal,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getPreloadedSimUpsellSummary: builder.mutation<any, any>({
      query: (body) => ({
        ...endpoints.mobileSimInventoryUpsellSummary,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    createPreloadedSimSubscription: builder.mutation<any, any>({
      query: (body) => ({
        ...endpoints.mobileSimInventoryCreateSubscription,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getPaymentMethods: builder.query<any, { customerId: string }>({
      query: (params) => ({
        ...endpoints.getPaymentMethods,
        params: params,
      }),
      providesTags: ["Activation"],
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    getReactivateOrderSummary: builder.query<any, any>({
      query: (params) => ({
        ...endpoints.reactivateOrderSummary,
        params,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
    reactivateCheckout: builder.mutation<any, any>({
      query: (body) => ({
        ...endpoints.reactivateCheckout,
        body,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => {
        return formatErrors(response.data);
      },
    }),
  }),
});

export const { actions: activationActions, reducer: activationReducer } = slice;

export const useActivationSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: slice.actions,
    ...api,
  };
};
